import React, { useRef, useState } from 'react';
import { carteraApi } from './api';
import { money } from './helpers';

// Tipos de producto. El back acepta el código corto (AP/AF/CS) y lo mapea al enum.
const TIPOS = [
  ['AP', 'Puro'],
  ['AF', 'Financiero'],
  ['CS', 'Crédito simple'],
];

const ESQUEMAS = [
  ['amortizable', 'Amortizable'],
  ['interes_solo', 'Interés-solo'],
];

const hoy = () => new Date().toISOString().slice(0, 10);

// Parseo tolerante a comas/símbolos: "1,288,887.00" → 1288887. Igual que el
// helper del modal de pagos (los <input> de monto son texto, no number).
const parseMonto = (s) => {
  const n = parseFloat(String(s ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Alta manual de un contrato de cartera. Captura los datos mínimos, llama a
 * POST /cartera y, al crear, ofrece abrir el detalle del nuevo contrato.
 *
 * Props:
 *  - onClose()        cierra el modal.
 *  - onSaved()        se llama tras crear con éxito (para refrescar la lista).
 *  - onOpen(id)       abre el detalle del contrato recién creado.
 */
export const NuevoContratoModal = ({ onClose, onSaved, onOpen }) => {
  const [tipo, setTipo] = useState('AF');
  const [clave, setClave] = useState('');
  const [cliente, setCliente] = useState('');
  // Autocomplete de cliente (patrón del wizard de Vencimientos): elegir uno
  // existente lo LIGA (user_id); escribir libre crea/reusa por nombre.
  const [clienteSel, setClienteSel] = useState(null); // {id, name, email, carros}
  const [matches, setMatches] = useState([]);
  const [showMatches, setShowMatches] = useState(false);
  const searchTimer = useRef(null);
  const [vehiculo, setVehiculo] = useState('');
  const [monto, setMonto] = useState('');
  // Valor factura del activo (c/IVA): alimenta el Portafolio Total y la analítica
  // AP. Sin él un AP no aparece en esas vistas, por eso se captura aquí.
  const [valorFactura, setValorFactura] = useState('');
  const [tasa, setTasa] = useState('');
  const [plazo, setPlazo] = useState('');
  const [fecha, setFecha] = useState(hoy());
  const [esquema, setEsquema] = useState('amortizable');
  const [residual, setResidual] = useState('');
  // AP: renta contratada (con ella se genera el calendario) + servicios que YA
  // van dentro de la renta (informativo, no se cobra aparte).
  const [renta, setRenta] = useState('');
  const [servRenta, setServRenta] = useState('');
  // AF/CS: servicios mensuales cobrados APARTE de la cuota (independientes;
  // pueden llenarse solo uno o ninguno). IVA auto 16% si no se edita.
  const [servSeguro, setServSeguro] = useState('');
  const [servPlacas, setServPlacas] = useState('');
  const [servGps, setServGps] = useState('');
  const [servIva, setServIva] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [creado, setCreado] = useState(null); // crédito devuelto por el back

  const esAP = tipo === 'AP';
  const esAF = tipo === 'AF';
  const esCS = tipo === 'CS';

  // Al escribir se suelta el cliente elegido (vuelve a ser alta/reuso por nombre).
  function onClienteChange(value) {
    setCliente(value);
    setClienteSel(null);
    setShowMatches(!!value.trim());
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) { setMatches([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await carteraApi.buscarClientes(value.trim());
        setMatches(res?.data?.data || []);
      } catch { setMatches([]); }
    }, 250);
  }

  function pickCliente(m) {
    setClienteSel(m);
    setCliente(m.name);
    setMatches([]);
    setShowMatches(false);
  }
  const montoNum = parseMonto(monto);
  const valorFacturaNum = parseMonto(valorFactura);
  const tasaNum = parseMonto(tasa);
  const residualNum = parseMonto(residual);
  const rentaNum = parseMonto(renta);
  const servRentaNum = parseMonto(servRenta);
  const segNum = parseMonto(servSeguro);
  const plaNum = parseMonto(servPlacas);
  const gpsNum = parseMonto(servGps);
  const servSuma = segNum + plaNum + gpsNum;
  // IVA de accesorios: auto 16% de la suma; editable si el negocio trae otro monto.
  const ivaAuto = Math.round(servSuma * 0.16 * 100) / 100;
  const ivaNum = servIva.trim() !== '' ? parseMonto(servIva) : ivaAuto;
  const servTotal = servSuma > 0 ? Math.round((servSuma + ivaNum) * 100) / 100 : 0;

  async function submit() {
    // Validación en cliente (el back revalida y devuelve 422 con mensaje claro).
    if (!cliente.trim()) { setError('El nombre del cliente es obligatorio.'); return; }
    if (montoNum <= 0) { setError('El monto financiado debe ser mayor a cero.'); return; }
    if (!plazo || Number(plazo) <= 0) { setError('El plazo en meses es obligatorio.'); return; }
    if (!fecha) { setError('La fecha del primer pago es obligatoria.'); return; }
    if (!esAP && tasaNum <= 0) { setError('La tasa anual es obligatoria para financiero y crédito simple.'); return; }
    if (esAF && residualNum > 0 && residualNum >= montoNum) {
      setError('El residual debe ser menor al monto financiado.'); return;
    }
    if (esAP && rentaNum <= 0) { setError('La renta mensual es obligatoria en arrendamiento puro.'); return; }
    if (esAP && servRentaNum > 0 && servRentaNum >= rentaNum) {
      setError('Los servicios incluidos deben ser menores a la renta.'); return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body = {
        tipo,
        clave_contrato: clave.trim() || null,
        user_id: clienteSel?.id ?? null, // elegido del buscador → se liga a ese user
        cliente_nombre: cliente.trim(),
        vehiculo: vehiculo.trim() || null,
        monto_financiado: montoNum,
        // Valor factura (c/IVA): clave para que un AP se refleje en el Portafolio
        // Total y la analítica AP. Se envía en cualquier tipo si viene capturado.
        valor_factura: valorFacturaNum > 0 ? valorFacturaNum : null,
        tasa_interes_anual: tasaNum > 0 ? tasaNum : null,
        plazo_meses: Number(plazo),
        fecha_primer_pago: fecha,
        esquema_pago: esAP ? null : esquema,
        // Residual aplica a AF y AP (leasing); CS amortiza a 0.
        residual: !esCS && residualNum > 0 ? residualNum : null,
        // AP: renta contratada + servicios incluidos (informativo).
        renta_mensual: esAP && rentaNum > 0 ? rentaNum : null,
        servicios_renta: esAP && servRentaNum > 0 ? servRentaNum : null,
        // AF/CS: servicios cobrados aparte (solo los llenados; IVA editado o null = auto).
        serv_seguro: !esAP && segNum > 0 ? segNum : null,
        serv_placas: !esAP && plaNum > 0 ? plaNum : null,
        serv_gps: !esAP && gpsNum > 0 ? gpsNum : null,
        serv_iva: !esAP && servSuma > 0 && servIva.trim() !== '' ? ivaNum : null,
      };
      const res = await carteraApi.crearContrato(body);
      const data = res?.data?.data ?? res?.data ?? null;
      setCreado(data);
      if (onSaved) onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo crear el contrato. Verifica los datos.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="cart-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cart-modal">
        <div className="cm-head">
          <h3>{creado ? 'Contrato creado' : 'Nuevo contrato'}</h3>
          <button className="cm-close" onClick={onClose}><i className="pi pi-times" /></button>
        </div>

        {creado ? (
          <>
            <div className="cm-result">
              <div className="cr-folio">{creado.clave_contrato || '—'}</div>
              <div className="cr-row"><span>Cliente</span><b>{creado.cliente || '—'}</b></div>
              {creado.user_id != null && (
                <div className="cr-row"><span>Ligado a</span><b>user #{creado.user_id}</b></div>
              )}
              <div className="cr-row"><span>Producto</span><b>{creado.tipo_label || creado.tipo}</b></div>
              <div className="cr-row"><span>Monto financiado</span><b>{money(creado.monto_financiado)}</b></div>
              {creado.plazo_meses != null && (
                <div className="cr-row"><span>Plazo</span><b>{creado.plazo_meses} meses</b></div>
              )}
              <div className="cr-row"><span>Saldo insoluto</span><b>{money(creado.saldo_insoluto)}</b></div>
              {Number(creado.servicios_mensual) > 0 && (
                <div className="cr-row"><span>Servicios /mes (aparte)</span><b>{money(creado.servicios_mensual)}</b></div>
              )}
              {creado.cuotas?.total > 0 && (
                <div className="cr-row">
                  <span>{esAP ? 'Calendario de rentas' : 'Tabla de amortización'}</span>
                  <b>{creado.cuotas.total} {esAP ? 'rentas' : 'parcialidades'}</b>
                </div>
              )}
            </div>
            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Cerrar</button>
              <button className="btn btn-green btn-sm" onClick={() => onOpen?.(creado.id)}>
                <i className="pi pi-arrow-right" />Ver contrato
              </button>
            </div>
          </>
        ) : (
          <>
            {error && <div className="cm-error">{error}</div>}

            <div className="cm-field">
              <label>Tipo de producto</label>
              <div className="cm-seg">
                {TIPOS.map(([v, l]) => (
                  <button key={v} className={tipo === v ? 'on' : ''} onClick={() => setTipo(v)}>{l}</button>
                ))}
              </div>
            </div>

            <div className="cm-row">
              <div className="cm-field" style={{ position: 'relative' }}>
                <label>Cliente</label>
                <input className="cm-input" type="text" value={cliente}
                  onChange={(e) => onClienteChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowMatches(false), 150)}
                  onFocus={() => setShowMatches(matches.length > 0)}
                  placeholder="Busca o escribe el nombre…" autoFocus />
                {showMatches && matches.length > 0 && (
                  <div className="cm-auto">
                    {matches.map((m) => (
                      <button key={m.id} type="button" className="cm-auto-item"
                        onMouseDown={(e) => { e.preventDefault(); pickCliente(m); }}>
                        <span className="fw6">{m.name}</span>
                        <span className="cm-auto-sub">{m.email} · {m.carros} operación{m.carros === 1 ? '' : 'es'}</span>
                      </button>
                    ))}
                  </div>
                )}
                {clienteSel ? (
                  <div className="cm-note" style={{ color: '#06794b', fontWeight: 600 }}>
                    Se ligará al cliente existente #{clienteSel.id} ({clienteSel.email})
                  </div>
                ) : cliente.trim().length >= 2 && (
                  <div className="cm-note">Cliente nuevo: se crea (o se reusa si el nombre coincide exacto).</div>
                )}
              </div>
              <div className="cm-field">
                <label>Clave de contrato (opcional)</label>
                <input className="cm-input" type="text" value={clave}
                  onChange={(e) => setClave(e.target.value)} placeholder="Se autogenera si se deja vacía" />
              </div>
            </div>

            <div className="cm-field">
              <label>Vehículo (opcional)</label>
              <input className="cm-input" type="text" value={vehiculo}
                onChange={(e) => setVehiculo(e.target.value)} placeholder="Marca, modelo, año…" />
            </div>

            <div className="cm-row">
              <div className="cm-field">
                <label>Monto financiado</label>
                <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                  value={monto} onChange={(e) => setMonto(e.target.value)} />
                {montoNum > 0 && <div className="cm-note">= {money(montoNum)}</div>}
              </div>
              <div className="cm-field">
                <label>Tasa anual %{esAP ? ' (opcional)' : ''}</label>
                <input className="cm-input" type="text" inputMode="decimal" placeholder="28"
                  value={tasa} onChange={(e) => setTasa(e.target.value)} />
              </div>
            </div>

            {/* Valor factura del activo (c/IVA). Para AP es lo que lo hace visible
                en el Portafolio Total y la analítica AP; opcional en AF/CS. */}
            <div className="cm-row">
              <div className="cm-field">
                <label>Valor factura c/IVA{esAP ? '' : ' (opcional)'}</label>
                <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                  value={valorFactura} onChange={(e) => setValorFactura(e.target.value)} />
                {valorFacturaNum > 0 ? (
                  <div className="cm-note">= {money(valorFacturaNum)} · s/IVA ≈ {money(valorFacturaNum / 1.16)}</div>
                ) : esAP && (
                  <div className="cm-note">Sin esto, el AP no aparece en el Portafolio Total ni en la analítica AP.</div>
                )}
              </div>
              <div className="cm-field" />
            </div>

            <div className="cm-row">
              <div className="cm-field">
                <label>Plazo (meses)</label>
                <input className="cm-input" type="number" min="1" step="1" placeholder="48"
                  value={plazo} onChange={(e) => setPlazo(e.target.value)} />
              </div>
              <div className="cm-field">
                <label>Fecha del primer pago</label>
                <input className="cm-input" type="date" value={fecha}
                  onChange={(e) => setFecha(e.target.value)} />
              </div>
            </div>

            {/* Esquema aplica a AF/CS (AP usa renta plana). Residual aplica a
                AF/AP (leasing); CS amortiza a 0. */}
            <div className="cm-row">
              {!esAP && (
                <div className="cm-field">
                  <label>Esquema de pago</label>
                  <div className="cm-seg">
                    {ESQUEMAS.map(([v, l]) => (
                      <button key={v} className={esquema === v ? 'on' : ''} onClick={() => setEsquema(v)}>{l}</button>
                    ))}
                  </div>
                </div>
              )}
              {!esCS && (
                <div className="cm-field">
                  <label>Residual (opcional)</label>
                  <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                    value={residual} onChange={(e) => setResidual(e.target.value)} />
                  {residualNum > 0 && (
                    <div className="cm-note">= {money(residualNum)}{esAF ? ' · la tabla cierra aquí' : ''}</div>
                  )}
                </div>
              )}
            </div>

            {/* AP: renta contratada (con ella se genera el calendario completo) +
                servicios INCLUIDOS en la renta (informativo, no se cobra aparte). */}
            {esAP && (
              <div className="cm-row">
                <div className="cm-field">
                  <label>Renta mensual</label>
                  <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                    value={renta} onChange={(e) => setRenta(e.target.value)} />
                  {rentaNum > 0 && (
                    <div className="cm-note">= {money(rentaNum)} · se generan {Number(plazo) > 0 ? plazo : '…'} rentas mensuales</div>
                  )}
                </div>
                <div className="cm-field">
                  <label>Servicios incluidos (opcional)</label>
                  <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                    value={servRenta} onChange={(e) => setServRenta(e.target.value)} />
                  {servRentaNum > 0 && rentaNum > servRentaNum && (
                    <div className="cm-note">
                      Informativo: Financiera {money(rentaNum - servRentaNum)} + Servicios {money(servRentaNum)} = la renta. No se cobra aparte.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AF/CS: servicios mensuales cobrados APARTE de la cuota. Campos
                independientes: puede llenarse solo uno o ninguno. */}
            {!esAP && (
              <>
                <div className="cm-row">
                  <div className="cm-field">
                    <label>Seguro /mes (opcional)</label>
                    <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                      value={servSeguro} onChange={(e) => setServSeguro(e.target.value)} />
                  </div>
                  <div className="cm-field">
                    <label>Placas /mes (opcional)</label>
                    <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                      value={servPlacas} onChange={(e) => setServPlacas(e.target.value)} />
                  </div>
                </div>
                <div className="cm-row">
                  <div className="cm-field">
                    <label>GPS /mes (opcional)</label>
                    <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                      value={servGps} onChange={(e) => setServGps(e.target.value)} />
                  </div>
                  <div className="cm-field">
                    <label>IVA accesorios</label>
                    <input className="cm-input" type="text" inputMode="decimal"
                      placeholder={servSuma > 0 ? `auto: ${ivaAuto.toFixed(2)}` : 'auto 16%'}
                      value={servIva} onChange={(e) => setServIva(e.target.value)} />
                  </div>
                </div>
                {servSuma > 0 && (
                  <div className="cm-note" style={{ marginTop: -6, marginBottom: 10 }}>
                    Servicios: {money(servTotal)}/mes (IVA {servIva.trim() !== '' ? 'manual' : 'auto 16%'} = {money(ivaNum)}) — se cobran junto con cada cuota.
                  </div>
                )}
              </>
            )}

            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>Cancelar</button>
              <button className="btn btn-green btn-sm" onClick={submit} disabled={submitting}>
                <i className={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'} />{submitting ? 'Creando…' : 'Crear contrato'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NuevoContratoModal;
